import { MatIcon } from '@angular/material/icon';
import { AfterViewInit, Component, OnInit } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MatButton, MatFabButton } from '@angular/material/button';
import { TabelaComponent } from '../../shared/tabela/tabela.component';
import {
  CurrencyPipe,
  DecimalPipe,
  formatDate,
  NgFor,
  NgIf,
  PercentPipe,
} from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CarregamentoService } from '../../services/carregamento/carregamento.service';
import {
  AcessoOfertaEducacionalStateService,
  CondicaoQualidadeStateService,
  EtapasDeEdicaoService,
  LocalizacaoService,
  MemoriaDeCalculoService,
  PrecoEncargosAdicionaisStateService,
} from '../../services';
import { MemoriaDeCalculoDto } from '../../dtos';
import { PlanejamentoOrcamentarioService } from '../../services/planejamento-orcamentario/planejamento-orcamentario-service';
import { VariacaoDimensaoOfertaDto } from '../../dtos/variacao-dimensao-oferta-dto';
import { DiferencaOrcamentoRealizadoProjetadoDto } from '../../dtos/diferenca-orcamento-realizado-projetado.dto';
import {
  LocalizacaoSelecionada,
  PlanoEducacional,
} from '../../interfaces/location.interface';
import { TipoSimulacao } from '../../enums/simulation-type.enum';

import { maskitoTransform } from '@maskito/core';
import { mask_nenhuma_casa_decimal } from '../../directives/mascaras';
import { LayoutPrincipalService } from '../../layouts/layout-principal/layout-principal.service';
import { LayoutResultadoService } from '../../layouts/layout-resultado/layout-resultado.service';
import { CsvUtils, TabelaCsv } from '../../utils/csv-utils';
import { baixarArquivo } from '../../utils/arquivo-utils';
import { TabelaSalasDeAulaComponent } from '../../shared/tabela-salas-de-aula/tabela-salas-de-aula.component';
import { HeaderInfo, PdfUtils } from '../../utils/pdf-utils';
import { LayoutIndicadoresService } from '../../layouts/layout-indicadores/layout-indicadores.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import {
  montarLinhasAdicionalModalidade,
  TabelaAdicionalModalidadeComponent,
} from '../../shared/tabela-adicional-modalidade/tabela-adicional-modalidade.component';
import {
  montarLinhasDetalhamentoDespesa,
  TabelaDetalhamentoDespesaComponent,
} from '../../shared/tabela-detalhamento-despesa/tabela-detalhamento-despesa.component';
import { LayoutEdicaoService } from '../../layouts/layout-edicao/layout-edicao.service';
import { AnosReferenciasService } from '../../services/anos-referencias/anos-referencias.service';
import { AnosReferenciasDto } from '../../dtos/anos-referencias.dto';

@Component({
  selector: 'app-relatorio-previsao-orcamentaria',
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    TabelaComponent,
    MatIcon,
    MatFabButton,
    MatButton,
    RouterLinkActive,
    RouterLink,
    DecimalPipe,
    PercentPipe,
    CurrencyPipe,
    MatProgressSpinnerModule,
    TabelaAdicionalModalidadeComponent,
    TabelaDetalhamentoDespesaComponent,
    TabelaSalasDeAulaComponent,
  ],
  templateUrl: './relatorio-previsao-orcamentaria.component.html',
  styleUrl: './relatorio-previsao-orcamentaria.component.scss',
})
export class RelatorioPrevisaoOrcamentariaComponent implements OnInit, AfterViewInit {
  public memoriaDeCalculo: MemoriaDeCalculoDto | undefined = undefined;

  public carregandoPdfSalasDeAula = false;
  private novosDadosForamAdicionados = false;

  get mostrarTabelaSalasDeAula(): boolean {
    return this.featureFlagService.funcionalidadeHabilitada('1472-salas-necessarias')
      && !!this.memoriaDeCalculo?.resultadoParte9_3ResultadoSalasNecessarias;
  }

  get mostrarAdicionalModalidade(): boolean {
    return this.featureFlagService.funcionalidadeHabilitada('995-fator-ponderacao-calculo.exibicao-fator-ponderacao')
      && !!this.memoriaDeCalculo?.resultadoMatriculasFatorPonderacao
      && !!this.memoriaDeCalculo?.resultadoDistribuicaoDespesaPorEtapa;
  }

  get mostrarDetalhamentoDespesa(): boolean {
    return this.featureFlagService.funcionalidadeHabilitada('995-fator-ponderacao-calculo.exibicao-fator-ponderacao')
      && !!this.memoriaDeCalculo?.resultadoDistribuicaoDespesaPorEtapa;
  }

  // #1232: com a flag 1104 (creche por idade) o back popula resultadoMatriculasPorEtapa
  // e deixa resultadoEtapa1NumerosDeMatriculas nulo — o que zerava o número de matrículas
  // no relatório. Este getter resolve a fonte correta; ambos os DTOs expõem os mesmos
  // agregados por etapa (creche/preEscola/ensFunAnosIniciais/ensFunAnosFinais/ensMedio/eja/total).
  get numerosDeMatriculas() {
    return this.featureFlagService.funcionalidadeHabilitada('1104-creche-idade') && this.memoriaDeCalculo?.resultadoMatriculasPorEtapa
      ? this.memoriaDeCalculo.resultadoMatriculasPorEtapa
      : this.memoriaDeCalculo?.resultadoEtapa1NumerosDeMatriculas;
  }

  get mostrarRpebAposVaat(): boolean {
    return this.featureFlagService.funcionalidadeHabilitada('1214-alteracoes-valor-aluno');
  }

  get colunasTabelaComplementacao(): number {
    return this.mostrarRpebAposVaat ? 5 : 4;
  }

  // Esfera Administrativa ocupa 20%; as colunas de valores dividem o restante igualmente.
  get larguraColunaValorComplementacao(): number {
    return 80 / (this.colunasTabelaComplementacao - 1);
  }

  get isPlanoRedeMunicipal(): boolean {
    return this.planoSelecionado?.value === TipoSimulacao.planejamentoPorEsferaAdmMunicipal;
  }

  get isPlanoRedeEstadual(): boolean {
    return this.planoSelecionado?.value === TipoSimulacao.planejamentoPorEsferaAdmEstadual;
  }

  isEstadualVazio(valor: number | undefined | null): boolean {
    return (this.isPlanoRedeMunicipal || this.isPlanoGeralMunicipio) && !valor;
  }

  isMunicipalVazio(valor: number | undefined | null): boolean {
    return this.isPlanoRedeEstadual && !valor;
  }

  get isPlanoGeral(): boolean {
    return this.planoSelecionado?.value === TipoSimulacao.planejamento;
  }

  get isPlanoGeralMunicipio(): boolean {
    return this.isPlanoGeral && !!this.localizacaoSelecionada?.cidadeSelecionada;
  }

  get complementacaoMunicipal(): number {
    if (this.isPlanoRedeEstadual) {
      return 0;
    }
    if (this.isPlanoGeralMunicipio) {
      const projecao = this.memoriaDeCalculo?.resultadoDespesaPessoalManutencao?.despesasGovernoMunicipal ?? 0;
      const receita = this.diferencaOrcamento?.receitaPotencialMunicipal ?? 0;
      return this.calcularComplementacao(projecao, receita);
    }
    return this.memoriaDeCalculo?.complementacaoDto?.complementacaoNivelMunicipio ?? 0;
  }

  get complementacaoEstadual(): number {
    if (this.isPlanoRedeMunicipal) {
      return 0;
    }
    return this.memoriaDeCalculo?.complementacaoDto?.complementacaoNivelEstado ?? 0;
  }

  // Dados baseados na localização selecionada
  public localizacaoSelecionada?: LocalizacaoSelecionada | null;
  public planoSelecionado: PlanoEducacional | undefined;
  public variacaoDimensaoOferta: VariacaoDimensaoOfertaDto | undefined;
  public diferencaOrcamento: DiferencaOrcamentoRealizadoProjetadoDto | undefined;

  // Estados de carregamento e erro
  public carregandoDados: boolean = true;
  public temDadosParaExibir: boolean = false;
  public anosReferencias: AnosReferenciasDto | null = null;


  constructor(
    private carregamentoService: CarregamentoService,
    private etapasDeEdicaoService: EtapasDeEdicaoService,
    private layoutPrincipalService: LayoutPrincipalService,
    private layoutResultadoService: LayoutResultadoService,
    private memoriaDeCalculoService: MemoriaDeCalculoService,
    private condicaoQualidadeStateService: CondicaoQualidadeStateService,
    private acessoOfertaEducacionalStateService: AcessoOfertaEducacionalStateService,
    private precoEncargosAdicionaisStateService: PrecoEncargosAdicionaisStateService,
    private localizacaoService: LocalizacaoService,
    private planejamentoOrcamentarioService: PlanejamentoOrcamentarioService,
    private layoutIndicadoresService: LayoutIndicadoresService,
    public featureFlagService: FeatureFlagService,
    private layoutEdicaoService: LayoutEdicaoService,
    private anosReferenciasService: AnosReferenciasService
  ) {
    if (this.featureFlagService.funcionalidadeHabilitada('938-migracao-datas-relatorios-1-e-2')) {
      this.anosReferencias = this.anosReferenciasService.obterAnosReferenciasSnapshot();
    }
  }

  async ngOnInit(): Promise<void> {
    this.carregandoDados = true;

    // Obter localização selecionada
    this.localizacaoSelecionada = this.localizacaoService.obterLocalizacaoSelecionada();

    // Verificar se localização foi selecionada
    if (!this.localizacaoSelecionada) {
      console.warn('Nenhuma localização selecionada. Redirecionando para seleção...');
      this.carregandoDados = false;
      // Aqui você pode adicionar redirecionamento para página de seleção
      return;
    }

    this.planoSelecionado = this.localizacaoService
      .obterOpcoesPlanoEducacional(this.localizacaoSelecionada!)
      .find(opcao => opcao.value === this.localizacaoSelecionada?.tipoSimulacaoSelecionado);

    this.definaConfiguracoesDaPagina();
    this.definaConfiguracoesIndicadores();

    this.etapasDeEdicaoService.habilitarOpcao(22);
    this.etapasDeEdicaoService.habilitarOpcao(23);

    try {

      this.definirRotaDownloadResultado();

      this.layoutResultadoService.setRotaEdicaoSimulacao('edicao-relatorio-planejamento-orcamentario/acesso-oferta-educacional/diagnostico-matricula');

      this.layoutResultadoService.setRotaApresentacao('/planejamento-orcamentario-apresentacao');

      this.layoutResultadoService.setTextoComoCitar(
        `SIMULADOR de Custo-Aluno Qualidade: Planejamento orçamentário, ${this.anosReferencias?.anoPqr ?? 2026}. Disponível em: <https://simcaq.ufg.br/>. Acesso em: ${formatDate(new Date(),
          'dd MMM yyyy', 'pt-BR')}.`);

      this.layoutPrincipalService.definaMensagensCarregamento([
        'Obtendo dados...',
        'Obtendo dados de salas de aula...',
        'Obtendo dados de professores...',
        'Obtendo dados de matrículas...'
      ]);

      setTimeout(() => {
        this.carregamentoService.startLoading();
      });

      this.novosDadosForamAdicionados =
        this.acessoOfertaEducacionalStateService.getProjecoesOfertaMatriculas()?.length > 0 ||
        this.condicaoQualidadeStateService.getPQRNumeroDiasLetivosValores()?.length > 0 ||
        this.condicaoQualidadeStateService.getPQRCargaHorarioEnsinoValores()?.length > 0 ||
        this.condicaoQualidadeStateService.getPQRTamanhoTurmasValores()?.length > 0 ||
        this.condicaoQualidadeStateService.getPQRFormacaoJornadaRemuneracaoProfessoresValores()?.length > 0 ||
        this.condicaoQualidadeStateService.getPQRQuadroFuncionariosRemuneracaoValores()?.length > 0 ||
        this.condicaoQualidadeStateService.getPQRManutencaoFormacaoMaterialValores()?.length > 0 ||
        this.condicaoQualidadeStateService.getPQRAlimentacaoEscolarValores()?.length > 0 ||
        this.condicaoQualidadeStateService.getPqrQuantidadeNovasConstrucoesValores()?.length > 0 ||
        this.precoEncargosAdicionaisStateService.getPQREncargosSociaisValores()?.length > 0 ||
        this.precoEncargosAdicionaisStateService.getPQRAdicionaisValores()?.length > 0 ||
        this.condicaoQualidadeStateService.getPQRFatorPonderacaoValores()?.length > 0;

      await Promise.all([
        // Buscando memória de cálculo (RECÁLCULO) baseada na localização selecionada na tela anterior
        (this.novosDadosForamAdicionados
          ? this.memoriaDeCalculoService.recalcularMemoriaDeCalculo()
          : this.memoriaDeCalculoService.buscarMemoriaDeCalculoLocalizacao()
        ).then((memoriaDeCalculo) => {
          this.memoriaDeCalculo = memoriaDeCalculo;
        })
          .catch((error) => {
            console.error('ERRO ao buscar memória de cálculo:', error);
            this.memoriaDeCalculo = undefined;
          }),

        // Buscando dados da localização
        this.planejamentoOrcamentarioService.buscarVariacaoDimensaoOfertaPorLocalizacao()
          .then((variacao) => (this.variacaoDimensaoOferta = variacao))
          .catch((error) => {
            console.warn('Erro ao buscar variação da dimensão da oferta:', error);
            this.variacaoDimensaoOferta = undefined;
          }),

        this.planejamentoOrcamentarioService.buscarDiferencaOrcamentoRealizadoProjetadoPorLocalizacao()
          .then((diferenca) => (this.diferencaOrcamento = diferenca))
          .catch((error) => {
            console.warn('Erro ao buscar diferença do orçamento:', error);
            this.diferencaOrcamento = undefined;
          }),
      ]);
    } catch (error) {
      console.error('Erro geral ao carregar dados do relatório:', error);
    } finally {
      this.carregamentoService.finishLoading();
      this.carregandoDados = false;
      if (this.diferencaOrcamento && this.planoSelecionado?.value === 2) {
        this.diferencaOrcamento.despesaRealizadaMunicipal = 0;
        this.diferencaOrcamento.receitaPotencialMunicipal = 0;
      }
      this.temDadosParaExibir = !!(this.memoriaDeCalculo && (this.variacaoDimensaoOferta || this.diferencaOrcamento));
    }
  }

  calcularVariacao(projecao: number, valorAtual: number): number {
    return projecao - valorAtual;
  }

  calcularComplementacao(projecao: number, receita: number): number {
    if (projecao > receita) return projecao - receita;
    return 0;
  }

  porcentagemDoValorNumeroReal(valor: number, total: number): number {
    return valor / total;
  }

  porcentagemDoValor(valor: number, total: number): number {
    return Math.abs(valor / total);
  }

  formatarPorcentagemDoValorOuTraco(valor: number, total: number): string {
    const percentual = this.porcentagemDoValor(valor, total) * 100;

    if (!Number.isFinite(percentual) || Math.abs(percentual) < 1e-9) {
      return '-';
    }

    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(percentual);
  }

  formatarDinheiro(valor: number | undefined): string {
    if (valor === undefined) return '0';
    return maskitoTransform(String(valor), mask_nenhuma_casa_decimal);
  }

  obterDespesaCorrenteAno(): number {
    if (!this.memoriaDeCalculo) return 0;
    return this.memoriaDeCalculo.resultadoDistribuicaoDespesaPorEtapa?.despesaNecessaria || 0;
  }

  calcularProjecaoDespesaCreche(): number {
    if (this.memoriaDeCalculo === undefined) return 0;
    const r = this.memoriaDeCalculo.resultadoDistribuicaoDespesaPorEtapa;
    if (!r) return 0;
    return (
      r.despesaNecessariaCrecheUrbanaParcial +
      r.despesaNecessariaCrecheUrbanaIntegral +
      r.despesaNecessariaCrecheUrbanaNoturna +
      r.despesaNecessariaCrecheRuralParcial +
      r.despesaNecessariaCrecheRuralIntegral +
      r.despesaNecessariaCrecheRuralNoturna
    );
  }

  calcularProjecaoDespesaPreEscola(): number {
    if (this.memoriaDeCalculo === undefined) return 0;
    const r = this.memoriaDeCalculo.resultadoDistribuicaoDespesaPorEtapa;
    if (!r) return 0;
    return (
      r.despesaNecessariaPreEscolaUrbanaParcial +
      r.despesaNecessariaPreEscolaUrbanaIntegral +
      r.despesaNecessariaPreEscolaUrbanaNoturna +
      r.despesaNecessariaPreEscolaRuralParcial +
      r.despesaNecessariaPreEscolaRuralIntegral +
      r.despesaNecessariaPreEscolaRuralNoturna
    );
  }

  calcularProjecaoDespesaEFAnosIniciais(): number {
    if (this.memoriaDeCalculo === undefined) return 0;
    const r = this.memoriaDeCalculo.resultadoDistribuicaoDespesaPorEtapa;
    if (!r) return 0;
    return (
      r.despesaNecessariaEnsFunAnosIniciaisUrbanaParcial +
      r.despesaNecessariaEnsFunAnosIniciaisUrbanaIntegral +
      r.despesaNecessariaEnsFunAnosIniciaisUrbanaNoturna +
      r.despesaNecessariaEnsFunAnosIniciaisRuralParcial +
      r.despesaNecessariaEnsFunAnosIniciaisRuralIntegral +
      r.despesaNecessariaEnsFunAnosIniciaisRuralNoturna
    );
  }

  calcularProjecaoDespesaEFAnosFinais(): number {
    if (this.memoriaDeCalculo === undefined) return 0;
    const r = this.memoriaDeCalculo.resultadoDistribuicaoDespesaPorEtapa;
    if (!r) return 0;
    return (
      r.despesaNecessariaEnsFunAnosFinaisUrbanaParcial +
      r.despesaNecessariaEnsFunAnosFinaisUrbanaIntegral +
      r.despesaNecessariaEnsFunAnosFinaisUrbanaNoturna +
      r.despesaNecessariaEnsFunAnosFinaisRuralParcial +
      r.despesaNecessariaEnsFunAnosFinaisRuralIntegral +
      r.despesaNecessariaEnsFunAnosFinaisRuralNoturna
    );
  }

  calcularProjecaoDespesaEM(): number {
    if (this.memoriaDeCalculo === undefined) return 0;
    const r = this.memoriaDeCalculo.resultadoDistribuicaoDespesaPorEtapa;
    if (!r) return 0;
    return (
      r.despesaNecessariaEnsMedioUrbanaParcial +
      r.despesaNecessariaEnsMedioUrbanaIntegral +
      r.despesaNecessariaEnsMedioUrbanaNoturna +
      r.despesaNecessariaEnsMedioRuralParcial +
      r.despesaNecessariaEnsMedioRuralIntegral +
      r.despesaNecessariaEnsMedioRuralNoturna
    );
  }

  calcularProjecaoDespesaEJA(): number {
    if (this.memoriaDeCalculo === undefined) return 0;
    const r = this.memoriaDeCalculo.resultadoDistribuicaoDespesaPorEtapa;
    if (!r) return 0;
    return (
      r.despesaNecessariaEJAUrbanaParcial +
      r.despesaNecessariaEJAUrbanaIntegral +
      r.despesaNecessariaEJAUrbanaNoturna +
      r.despesaNecessariaEJARuralParcial +
      r.despesaNecessariaEJARuralIntegral +
      r.despesaNecessariaEJARuralNoturna
    );
  }

  definaConfiguracoesDaPagina() {
    this.layoutResultadoService.setTitulo('Orçamento para garantia de um padrão de qualidade');
    this.layoutResultadoService.setCorTexto('text-blue-600');
    this.layoutResultadoService.setCorBordaTitulo('border-blue-600');

    // Permite edição para todos os tipos de simulação
    // O bloqueio específico da seção de Novas Salas é feito no componente próprio
    this.layoutResultadoService.setEditavel(true);
    this.layoutResultadoService.setRotaResultado('/resultado/projecao-orcamentaria');
    this.layoutResultadoService.setRotaFiltroLocalidade('/selecionar-local');
    this.layoutResultadoService.setRotaIndicadoresLocais('/indicadores/projecao-orcamentaria');

    if (this.planoSelecionado?.label) {
      this.layoutResultadoService.setRedeSelecionada(this.planoSelecionado.label);
    }

    this.layoutResultadoService.setPais('Brasil');

    if (this.localizacaoSelecionada?.estadoSelecionado) {
      this.layoutResultadoService.setEstado(this.localizacaoSelecionada.estadoSelecionado.sigla);
    }

    if (this.localizacaoSelecionada?.cidadeSelecionada?.label) {
      this.layoutResultadoService.setCidade(this.localizacaoSelecionada.cidadeSelecionada.label);
    }

    this.layoutResultadoService.setAno(String(this.anosReferencias?.anoPqr ?? 2026));

    this.layoutResultadoService.setRotaIndicadoresLocais('/indicadores/orcamento-educacional');

    this.layoutEdicaoService.setTituloEdicao("Planejamento orçamentário");
    this.layoutEdicaoService.setCorTituloEdicao("text-blue-600");
    this.layoutEdicaoService.setCorBordaEdicao("border-blue-600");
  }

  definaConfiguracoesIndicadores() {
    this.layoutIndicadoresService.setTituloTag('Planejamento orçamentário');
    this.layoutIndicadoresService.setCorBordaTag('border-blue-600');
    this.layoutIndicadoresService.setCorTextoTag('text-blue-600');
    this.layoutIndicadoresService.setRotaResultado('/resultado/projecao-orcamentaria');
    this.layoutIndicadoresService.setRotaFiltroLocalidade('/selecionar-local');
    this.layoutIndicadoresService.setRotaApresentacao('/planejamento-orcamentario-apresentacao');

    // Mesma lógica de bloqueio para o layout de indicadores
    const tiposPermitidosEdicao = [
      TipoSimulacao.planejamentoPorEsferaAdmEstadual,
      TipoSimulacao.planejamentoPorEsferaAdmMunicipal
    ];
    const podeEditar = this.planoSelecionado?.value && tiposPermitidosEdicao.includes(this.planoSelecionado.value);

    this.layoutIndicadoresService.setEditavel(!!podeEditar);
  }

  private definirRotaDownloadResultado() {
    const localizacao = this.localizacaoSelecionada;

    if (localizacao?.cidadeSelecionada) {
      this.layoutResultadoService.setRotaDownloadResultado(
        `planejamento-orcamentario/municipio/${localizacao.cidadeSelecionada.value}/csv`
      );
      return;
    }

    if (localizacao?.estadoSelecionado) {
      this.layoutResultadoService.setRotaDownloadResultado(
        `planejamento-orcamentario/estado/${localizacao.estadoSelecionado.value}/csv`
      );
      return;
    }

    this.layoutResultadoService.setRotaDownloadResultado('planejamento-orcamentario/nacional/csv');

    this.layoutResultadoService.setCorTextoRodape('text-blue-600');
    this.layoutResultadoService.setCorBotaoRodape('bg-blue-600');
    this.layoutResultadoService.setCorBordaBotaoRodape('border-blue-600');
  }

  ngAfterViewInit(): void {
    // Registra os downloads do rodapé depois que a view foi inicializada
    setTimeout(() => {
      this.configurarDownloads();
    }, 1000);
  }

  private configurarDownloads(): void {
    const downloads = [
      {
        tipo: 'acao' as const,
        texto: 'Download PDF completo',
        acao: () => this.baixarPdfCompleto()
      },
      {
        tipo: 'acao' as const,
        texto: 'Download CSV completo',
        acao: () => this.baixarCsvCompleto()
      }
    ];

    this.layoutResultadoService.setDownloads(downloads);
  }

  // CSVs: cada tabela é montada uma única vez, com título, rótulos e colunas iguais aos da tela,
  // e reaproveitada tanto no download individual quanto no CSV completo.
  baixarCsvDimensaoOferta(): void {
    this.baixarCsvTabela(this.tabelaDimensaoOfertaCsv());
  }

  baixarCsvFuncionarios(): void {
    this.baixarCsvTabela(this.tabelaFuncionariosCsv());
  }

  baixarCsvDespesaComplementacao(): void {
    this.baixarCsvTabela(this.tabelaComplementacaoCsv());
  }

  baixarCsvDetalhamentoDespesa(): void {
    this.baixarCsvTabela(this.tabelaDetalhamentoDespesaCsv());
  }

  baixarCsvAdicionalModalidade(): void {
    this.baixarCsvTabela(this.tabelaAdicionalModalidadeCsv());
  }

  baixarCsvMatriculasDespesas(): void {
    this.baixarCsvTabela(this.tabelaMatriculasDespesasCsv());
  }

  baixarCsvCompleto(): void {
    CsvUtils.baixarTabelas(this.tabelasCsv(), 'Relatorio_Previsao_Orcamentaria_Completo', this.anoArquivoCsv, this.obterTextoLocalizacao());
  }

  // Mesma ordem e mesmas condições de exibição da tela.
  tabelasCsv(): TabelaCsv[] {
    return [
      this.tabelaDimensaoOfertaCsv(),
      this.tabelaFuncionariosCsv(),
      this.tabelaComplementacaoCsv(),
      this.tabelaDetalhamentoDespesaCsv(),
      this.tabelaAdicionalModalidadeCsv(),
      this.tabelaDespesaCapitalCsv(),
      this.tabelaMatriculasDespesasCsv(),
    ].filter((tabela): tabela is TabelaCsv => tabela !== null);
  }

  private baixarCsvTabela(tabela: TabelaCsv | null): void {
    if (tabela) {
      CsvUtils.baixar(tabela, this.anoArquivoCsv, this.obterTextoLocalizacao());
    }
  }

  private get anoArquivoCsv(): string {
    return String(this.anosReferencias?.anoDadosFinanceiros ?? 2026);
  }

  private get anoCenso(): number {
    return this.anosReferencias?.anoCenso ?? 2025;
  }

  private get anoPqr(): number {
    return this.anosReferencias?.anoPqr ?? 2026;
  }

  get numeroTurmasProjetado(): number {
    const calcNumTurmas = this.featureFlagService.funcionalidadeHabilitada('1108-pqr-minimo-turma')
      && this.featureFlagService.funcionalidadeHabilitada('1104-creche-idade')
      ? this.memoriaDeCalculo?.resultadoCalcNumTurmas
      : undefined;
    return (calcNumTurmas ? calcNumTurmas.total : this.memoriaDeCalculo?.resultadoEtapa4CalculoNumeroTurmas?.total) ?? 0;
  }

  get numeroProfessoresProjetado(): number {
    return (this.featureFlagService.funcionalidadeHabilitada('1104-creche-idade')
      ? this.memoriaDeCalculo?.resultadoCalcNumProf?.total
      : this.memoriaDeCalculo?.resultadoEtapa10CalculoDemandaPorDocentes?.total) ?? 0;
  }

  private tabelaDimensaoOfertaCsv(): TabelaCsv | null {
    const atual = this.variacaoDimensaoOferta;
    if (!atual || !this.memoriaDeCalculo) return null;

    const linha = (dimensao: string, valorAtual: number, projecao: number) => ({
      dimensao,
      atual: valorAtual,
      projecao,
      variacao: this.porcentagemDoValorNumeroReal(this.calcularVariacao(projecao, valorAtual), valorAtual) * 100,
    });

    return {
      titulo: 'Diagnóstico e projeção da oferta educacional',
      nome: 'Dimensao_Oferta_Educacional',
      cabecalho: [
        { chave: 'dimensao', rotulo: 'Dimensão da oferta' },
        { chave: 'atual', rotulo: `Atual - ${this.anoCenso}`, tipo: 'inteiro' },
        { chave: 'projecao', rotulo: `Projeção - ${this.anoPqr}`, tipo: 'inteiro' },
        { chave: 'variacao', rotulo: `Variação ${this.anoCenso}-${this.anoPqr}`, tipo: 'percentual' },
      ],
      dados: [
        linha('Número de matrículas', atual.numeroMatriculas, this.numerosDeMatriculas?.total ?? 0),
        linha('Número de turmas', atual.numeroTurmas, this.numeroTurmasProjetado),
        linha('Número de professores', atual.numeroDocentes, this.numeroProfessoresProjetado),
        { dimensao: 'Número de auxiliares', atual: atual.numeroAuxiliares, projecao: '-', variacao: '-' },
        linha('Número de funcionários', atual.numeroFuncionarios, this.memoriaDeCalculo.resultadoEtapa18ProjecaoNumeroFuncionarios.total),
      ],
    };
  }

  private tabelaFuncionariosCsv(): TabelaCsv | null {
    if (!this.memoriaDeCalculo || !this.variacaoDimensaoOferta) return null;

    return {
      titulo: 'Projeção do número de funcionários',
      nome: 'Projecao_Funcionarios',
      cabecalho: [
        { chave: 'area', rotulo: 'Área de atuação' },
        { chave: 'atual', rotulo: `Número de funcionários - ${this.anoCenso}`, tipo: 'inteiro' },
        { chave: 'projecao', rotulo: `Projeção do número necessário de funcionários - ${this.anoPqr}`, tipo: 'inteiro' },
      ],
      dados: [
        ...this.funcionariosOrdenados.map((funcao) => ({ area: funcao.nomeFuncao, atual: '', projecao: funcao.quantidade })),
        {
          area: 'Total',
          atual: this.variacaoDimensaoOferta.numeroFuncionarios,
          projecao: this.memoriaDeCalculo.resultadoEtapa18ProjecaoNumeroFuncionarios.total,
        },
      ],
    };
  }

  private tabelaComplementacaoCsv(): TabelaCsv | null {
    const orcamento = this.diferencaOrcamento;
    if (!orcamento || !this.memoriaDeCalculo) return null;

    const rotuloComplementacao = `${this.mostrarRpebAposVaat ? 'Complementação VAAT-CAQ' : 'Complementação'} - ${this.anoPqr}`;
    const receitaEstadualVazia = this.isEstadualVazio(orcamento.receitaPotencialEstadual);
    const receitaMunicipalVazia = this.isMunicipalVazio(orcamento.receitaPotencialMunicipal);
    const complementacaoEstadualVazia = this.isEstadualVazio(this.complementacaoEstadual);
    const complementacaoMunicipalVazia = this.isMunicipalVazio(this.complementacaoMunicipal);

    // Mesmos traços da tela quando a esfera não faz parte do plano selecionado.
    const linha = (esfera: string, receita: number, complementacao: number, receitaVazia: boolean, complementacaoVazia: boolean) => ({
      esfera,
      receita: receitaVazia ? '-' : receita,
      complementacao: complementacaoVazia ? '-' : complementacao,
      percentual: complementacaoVazia ? '-' : this.percentualComplementacao(complementacao, receita),
      rpebAposVaat: receitaVazia ? '-' : receita + complementacao,
    });

    return {
      titulo: 'Projeção da complementação VAAT-CAQ',
      nome: 'Despesa_Complementacao',
      cabecalho: [
        { chave: 'esfera', rotulo: 'Esfera Administrativa' },
        { chave: 'receita', rotulo: `Receita Potencial mínima Vinculada à Educação (RPEB) (R$) - ${this.anoPqr}`, tipo: 'moeda' },
        { chave: 'complementacao', rotulo: `${rotuloComplementacao} (R$)`, tipo: 'moeda' },
        { chave: 'percentual', rotulo: `${rotuloComplementacao} (%)`, tipo: 'percentual' },
        ...(this.mostrarRpebAposVaat
          ? [{ chave: 'rpebAposVaat', rotulo: `RPEB após VAAT-CAQ - ${this.anoPqr}`, tipo: 'moeda' as const }]
          : []),
      ],
      dados: [
        linha('Governo Estadual', orcamento.receitaPotencialEstadual, this.complementacaoEstadual,
          receitaEstadualVazia, complementacaoEstadualVazia),
        linha('Governo Municipal', orcamento.receitaPotencialMunicipal, this.complementacaoMunicipal,
          receitaMunicipalVazia, complementacaoMunicipalVazia),
        linha('Total', this.totalReceitaPotencialMinimaVinculada, this.totalComplementacao,
          receitaEstadualVazia || receitaMunicipalVazia, complementacaoEstadualVazia || complementacaoMunicipalVazia),
      ],
    };
  }

  private tabelaDetalhamentoDespesaCsv(): TabelaCsv | null {
    if (!this.mostrarDetalhamentoDespesa) return null;

    return {
      titulo: 'Detalhamento da despesa necessária para financiar o CAQ',
      nome: 'Detalhamento_Despesa_CAQ',
      cabecalho: [
        { chave: 'descricao', rotulo: 'Itens de despesa' },
        { chave: 'valor', rotulo: 'Valor (R$)', tipo: 'moeda' },
        { chave: 'percentual', rotulo: '%', tipo: 'percentual' },
      ],
      // O recuo das subcategorias na tela vira espaços à esquerda no CSV.
      dados: montarLinhasDetalhamentoDespesa(this.memoriaDeCalculo, true)
        .map((linha) => ({ ...linha, descricao: '  '.repeat(linha.nivelRecuo) + linha.descricao })),
    };
  }

  private tabelaAdicionalModalidadeCsv(): TabelaCsv | null {
    if (!this.mostrarAdicionalModalidade) return null;

    return {
      titulo: 'Adicional para modalidades e contextos',
      nome: 'Adicional_Modalidades_Contextos',
      cabecalho: [
        { chave: 'item', rotulo: 'Item' },
        { chave: 'matriculas', rotulo: 'Número de matrículas', tipo: 'inteiro' },
        { chave: 'valor', rotulo: 'Valor adicionado no orçamento necessário (R$)', tipo: 'moeda' },
      ],
      dados: montarLinhasAdicionalModalidade(
        this.memoriaDeCalculo?.resultadoMatriculasFatorPonderacao,
        this.memoriaDeCalculo?.resultadoDistribuicaoDespesaPorEtapa,
      ),
    };
  }

  private tabelaDespesaCapitalCsv(): TabelaCsv | null {
    if (!this.featureFlagService.funcionalidadeHabilitada('novas-salas') || !this.memoriaDeCalculo) return null;

    const relatorioCaq = this.memoriaDeCalculo.relatorioCaq;
    const total = relatorioCaq?.projecaoDespesaConstrucaoNovosPrediosTotal;
    const educacaoInfantil = (relatorioCaq?.projecaoDespesaConstrucaoNovosPrediosEdInfantilGovernoEstadual ?? 0)
      + (relatorioCaq?.projecaoDespesaConstrucaoNovosPrediosEdInfantilGovernoMunicipal ?? 0);
    const ensFundMedioEja = (relatorioCaq?.projecaoDespesaConstrucaoNovosPrediosEnsFunEnsMedioEjaGovernoEstadual ?? 0)
      + (relatorioCaq?.projecaoDespesaConstrucaoNovosPrediosEnsFunEnsMedioEjaGovernoMunicipal ?? 0);

    return {
      titulo: 'Projeção da despesa de capital (construção de novos prédios)',
      nome: 'Despesa_Capital_Novos_Predios',
      cabecalho: [
        { chave: 'esfera', rotulo: 'Esfera Administrativa' },
        { chave: 'total', rotulo: 'Investimento Total (R$)', tipo: 'moeda' },
        { chave: 'educacaoInfantil', rotulo: 'Educação Infantil (R$)', tipo: 'moeda' },
        { chave: 'educacaoInfantilPercentual', rotulo: 'Educação Infantil (%)' },
        { chave: 'ensFundMedioEja', rotulo: 'Ens. Fund. / Médio / EJA (R$)', tipo: 'moeda' },
        { chave: 'ensFundMedioEjaPercentual', rotulo: 'Ens. Fund. / Médio / EJA (%)' },
      ],
      dados: [{
        esfera: this.planoSelecionado?.label || 'Total',
        total: total ?? 0,
        educacaoInfantil,
        educacaoInfantilPercentual: this.formatarPorcentagemDoValorOuTraco(educacaoInfantil, total ?? 1),
        ensFundMedioEja,
        ensFundMedioEjaPercentual: this.formatarPorcentagemDoValorOuTraco(ensFundMedioEja, total ?? 1),
      }],
    };
  }

  private tabelaMatriculasDespesasCsv(): TabelaCsv | null {
    if (!this.memoriaDeCalculo) return null;

    const matriculas = this.numerosDeMatriculas;
    const despesaTotal = this.obterDespesaCorrenteAno();
    const linha = (etapa: string, numeroMatriculas: number | undefined, despesa: number) => ({
      etapa,
      matriculas: numeroMatriculas,
      despesa,
      percentual: this.porcentagemDoValor(despesa, despesaTotal) * 100,
    });

    return {
      titulo: 'Projeção das matrículas e da despesa por etapa/modalidade',
      nome: 'Matriculas_Despesas_Etapa',
      cabecalho: [
        { chave: 'etapa', rotulo: 'Etapa / Modalidade' },
        { chave: 'matriculas', rotulo: `Projeção das matrículas - ${this.anoPqr}`, tipo: 'inteiro' },
        { chave: 'despesa', rotulo: `Projeção da despesa necessária (R$) - ${this.anoPqr}`, tipo: 'moeda' },
        { chave: 'percentual', rotulo: '%', tipo: 'percentual' },
      ],
      dados: [
        linha('Creche', matriculas?.creche, this.calcularProjecaoDespesaCreche()),
        linha('Pré-Escola', matriculas?.preEscola, this.calcularProjecaoDespesaPreEscola()),
        linha('Ensino Fundamental - anos iniciais', matriculas?.ensFunAnosIniciais, this.calcularProjecaoDespesaEFAnosIniciais()),
        linha('Ensino Fundamental - anos finais', matriculas?.ensFunAnosFinais, this.calcularProjecaoDespesaEFAnosFinais()),
        linha('Ensino Médio', matriculas?.ensMedio, this.calcularProjecaoDespesaEM()),
        linha('EJA', matriculas?.eja, this.calcularProjecaoDespesaEJA()),
        // Na tela a linha Total não exibe percentual.
        { etapa: 'Total', matriculas: matriculas?.total, despesa: despesaTotal },
      ],
    };
  }

  async baixarPdfSalasDeAula(): Promise<void> {
    this.carregandoPdfSalasDeAula = true;
    try {
      const pdf = await this.memoriaDeCalculoService.baixarPdfSalasDeAula(this.novosDadosForamAdicionados);
      // Em caso de erro o CaqRequestService já exibe o aviso e devolve um arquivo vazio.
      if (pdf.size > 0) {
        baixarArquivo(pdf, 'Salas_de_Aula.pdf');
      }
    } finally {
      this.carregandoPdfSalasDeAula = false;
    }
  }

  // Métodos para o PDF completo
  async baixarPdfCompleto(): Promise<void> {
    const container = document.querySelector('.container') as HTMLElement;
    if (!container) {
      console.error('Container não encontrado para geração do PDF');
      return;
    }

    try {
      const localizacao = this.obterTextoLocalizacao();
      const headerInfo = this.criarHeaderInfo();
      await PdfUtils.baixarElementoComoPdf(container, 'Relatorio_Previsao_Orcamentaria_Completo', 'Planejamento Orçamentário', localizacao, String(this.anosReferencias?.anoDadosFinanceiros ?? 2026), headerInfo);
    } catch (error) {
      console.error('Erro ao gerar PDF completo:', error);
    }
  }

  get funcionariosOrdenados() {
    if (!this.memoriaDeCalculo?.resultadoEtapa18ProjecaoNumeroFuncionarios?.funcionariosPorFuncao) {
      return [];
    }
    return [...this.memoriaDeCalculo.resultadoEtapa18ProjecaoNumeroFuncionarios.funcionariosPorFuncao]
      .sort((a, b) => a.codTipoFuncao - b.codTipoFuncao);
  }

  private criarHeaderInfo(): HeaderInfo {
    const localizacao = this.localizacaoSelecionada;

    return {
      titulo: 'Planejamento orçamentário',
      tituloModo: 'Resultados',
      subtitulo: 'Resultados das projeções do SimCAQ para a localidade',
      ilustracao: this.layoutResultadoService.getImagemFundoAtual(),
      breadcrumb: {
        plano: this.planoSelecionado?.label || 'Plano não selecionado',
        pais: 'Brasil',
        estado: localizacao?.estadoSelecionado?.sigla || 'BR',
        cidade: localizacao?.cidadeSelecionada?.label,
        ano: String(this.anosReferencias?.anoPqr ?? 2026)
      }
    };
  }

  private obterTextoLocalizacao(): string {
    const localizacao = this.localizacaoSelecionada;
    if (localizacao?.cidadeSelecionada) {
      return `${localizacao.cidadeSelecionada.label} - ${localizacao.estadoSelecionado?.sigla}`;
    }
    if (localizacao?.estadoSelecionado) {
      return localizacao.estadoSelecionado.sigla;
    }
    return 'Brasil';
  }

  get totalComplementacao(): number {
    return this.complementacaoMunicipal + this.complementacaoEstadual;
  }

  get totalReceitaPotencialMinimaVinculada(): number {
    return (this.diferencaOrcamento?.receitaPotencialMunicipal ?? 0) + (this.diferencaOrcamento?.receitaPotencialEstadual ?? 0);
  }

  percentualComplementacao(complementacao: number, receita: number | null | undefined): number {
    return receita ? (complementacao * 100) / receita : 0;
  }

}
